import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  Signal,
  viewChild,
} from '@angular/core';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';
import {Meta} from '@angular/platform-browser';
import {EventType, NavigationCancellationCode, Router, RouterOutlet} from '@angular/router';
import {APP_SIGNALS} from '@app/core/tokens/app-signals.token';
import {HeaderComponent} from '@app/navigation/header/header.component';
import {NavrailComponent} from '@app/navigation/navrail/navrail.component';
import {AuthenticationService} from '@authentication/authentication.service';
import {ConfigurationResponse} from '@middleware';
import {WindowService} from '@services/window/window.service';
import {INSTANCE_NAME_PLACEHOLDER, SEO_BOTS_META_PLACEHOLDER} from '@shared/constants/constants';
import {filter} from 'rxjs';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, HeaderComponent, NavrailComponent, MatSnackBarModule],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App implements OnInit, OnDestroy {
  readonly title = signal(INSTANCE_NAME_PLACEHOLDER);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly appSignals = inject(APP_SIGNALS);
  private readonly expiryCheckInterval: any;

  private readonly appConfig: Signal<ConfigurationResponse>;
  private readonly instanceName: Signal<string>;
  private readonly seoBotsMeta: Signal<string>;

  private readonly nav = viewChild.required(NavrailComponent);

  constructor(
    private readonly authService: AuthenticationService,
    private readonly ngZone: NgZone,
    private readonly snackbar: MatSnackBar,
    private readonly router: Router,
    private readonly metaService: Meta,
    public readonly windowService: WindowService, // Needed for tests
  ) {
    this.appConfig = this.appSignals.appConfig;
    this.instanceName = this.appSignals.instanceName;
    this.seoBotsMeta = this.appSignals.seoBotsMeta;

    effect(() => {
      const instanceNameValue = this.instanceName();
      this.title.set(instanceNameValue);

      if (instanceNameValue && instanceNameValue !== INSTANCE_NAME_PLACEHOLDER) {
        this.replaceTitlePlaceholders(instanceNameValue);
      }
    });

    effect(() => {
      const seoBotsValue = this.seoBotsMeta();
      if (seoBotsValue && seoBotsValue !== SEO_BOTS_META_PLACEHOLDER) {
        this.replaceSeoMetaPlaceholders(seoBotsValue);
      }
    });
  }

  private replaceTitlePlaceholders(newName: string): void {
    const placeholderRegex = new RegExp(INSTANCE_NAME_PLACEHOLDER, 'g');

    const currentTitle = this.document.title;
    if (currentTitle.includes(INSTANCE_NAME_PLACEHOLDER)) {
      this.document.title = currentTitle.replace(placeholderRegex, newName);
    }
  }

  private replaceSeoMetaPlaceholders(newMetaValue: string): void {
    const placeholderRegex = new RegExp(SEO_BOTS_META_PLACEHOLDER, 'g');
    this.updateMetaTag('name="robots"', placeholderRegex, newMetaValue, SEO_BOTS_META_PLACEHOLDER);
  }

  private updateMetaTag(selector: string, placeholderRegex: RegExp, newValue: string, placeholder: string): void {
    const metaTag = this.metaService.getTag(selector);
    const currentMetaContent = metaTag ? metaTag.content : '';

    if (currentMetaContent.includes(placeholder)) {
      const definition: {[key: string]: string} = {
        content: currentMetaContent.replace(placeholderRegex, newValue),
      };

      if (metaTag) {
        if (metaTag.name) {
          definition['name'] = metaTag.name;
        } else {
          const propertyValue = metaTag.getAttribute('property');
          if (propertyValue) {
            definition['property'] = propertyValue;
          }
        }
      }

      this.metaService.updateTag(definition);
    }
  }

  // Note: SonarQube is wrong about making this `readonly`
  public static suppressGuardRejectionMessage: boolean = false;

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    this.ngZone.runOutsideAngular(() => {
      const waitForToken = setInterval(() => {
        if (this.authService.token()) {
          clearInterval(waitForToken);
          this.authService.scheduleTokenExpiryCheck();
        }
      }, 500);
    });

    // This appears to be unused to SonarQube, but the subscription is needed for
    // listening to `NavigationCancel` events
    const routeErrorSubscription = this.router.events
      .pipe(filter(event => event.type == EventType.NavigationCancel))
      .subscribe((event: any) => {
        if (
          event.code == NavigationCancellationCode.GuardRejected ||
          event.code == NavigationCancellationCode.NoDataFromResolver
        ) {
          if (App.suppressGuardRejectionMessage) {
            App.suppressGuardRejectionMessage = false;
          } else {
            this.snackbar.open(
              'The requested page does not exist or you do not have permission to access it.',
              undefined,
              {duration: 5000, panelClass: 'snackbar-error'},
            );
          }
        }
      });
  }

  ngOnDestroy() {
    if (this.expiryCheckInterval) {
      clearInterval(this.expiryCheckInterval);
    }
  }

  toggleNav() {
    const nav = this.nav();
    if (nav) {
      nav.manuallyExpanded = !nav.manuallyExpanded;
    }
  }
}
